import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { sendChatMessage, getChatHistory, clearChatHistory, getUserConversations, recordPageVisit } from "@/lib/api";
import { getSessionId, formatTime } from "@/lib/utils";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import { 
  Send, 
  Loader2, 
  Heart, 
  User, 
  Trash2,
  MessageCircle,
  BookOpen,
  Users,
  Stethoscope,
  LogIn,
  History,
  Mic,
  MicOff,
  Square,
  Volume2,
  VolumeX,
  HelpCircle
} from "lucide-react";

// Chat contexts - using translation keys for labels/descriptions
const getChatContexts = (t) => [
  { 
    id: "goals_of_care", 
    label: t('chat.goalsOfCare', 'Goals of Care'),
    icon: Heart,
    description: t('chat.goalsDescription', 'Discuss end-of-life wishes and advance care planning'),
    welcomeMessage: t('chat.goalsWelcome', "Hello, I'm here to help you navigate goals of care and advanced care planning conversations. These discussions can be difficult, but they're so important. What would you like to talk about today?")
  },
  { 
    id: "hospice_education", 
    label: t('chat.hospiceInfo', 'Hospice Info'),
    icon: BookOpen,
    description: t('chat.hospiceDescription', 'Learn about hospice care and its benefits'),
    welcomeMessage: t('chat.hospiceWelcome', "Welcome! I'm here to help you understand hospice care - what it is, how it helps, and what to expect. What questions do you have about hospice?")
  },
  { 
    id: "caregiver_support", 
    label: t('chat.caregiverSupport', 'Caregiver Support'),
    icon: Users,
    description: t('chat.caregiverDescription', 'Get support and resources for caregivers'),
    welcomeMessage: t('chat.caregiverWelcome', "Hello, caregiver. I want you to know that what you're doing matters deeply. I'm here to support you with resources, tips, and a compassionate ear. How can I help you today?")
  },
  { 
    id: "symptom_guide", 
    label: t('chat.symptomHelp', 'Symptom Help'),
    icon: Stethoscope,
    description: t('chat.symptomDescription', 'Get guidance on managing symptoms and when to call'),
    welcomeMessage: t('chat.symptomWelcome', "I'm here to help you understand and manage hospice-related symptoms. I'll provide guidance on comfort measures and clearly tell you when to call your hospice team. What symptom or concern would you like to discuss?")
  }
];

export default function Chat() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const chatContexts = getChatContexts(t);
  const [activeContext, setActiveContext] = useState("goals_of_care");
  const [messages, setMessages] = useState({});
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(getSessionId());
  const [conversations, setConversations] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Voice input state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  
  // Text-to-speech state
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const speechSynthRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    recordPageVisit("chat").catch(console.error);
    loadChatHistory();
    if (user) {
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, activeContext]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadChatHistory = async () => {
    try {
      const data = await getChatHistory(sessionId);
      const grouped = {};
      chatContexts.forEach(ctx => {
        grouped[ctx.id] = data.messages.filter(m => m.context === ctx.id);
      });
      setMessages(grouped);
    } catch (error) {
      console.error("Error loading chat history:", error);
    }
  };

  const loadConversations = async () => {
    try {
      const data = await getUserConversations();
      setConversations(data.conversations);
    } catch (error) {
      console.error("Error loading conversations:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: "user",
      content: inputMessage,
      context: activeContext,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => ({
      ...prev,
      [activeContext]: [...(prev[activeContext] || []), userMessage]
    }));
    setInputMessage("");
    setIsLoading(true);

    try {
      const response = await sendChatMessage(inputMessage, sessionId, activeContext);
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: response.response,
        context: activeContext,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => ({
        ...prev,
        [activeContext]: [...(prev[activeContext] || []), assistantMessage]
      }));
      
      // Speak the AI response if TTS is enabled
      if (ttsEnabled && response.response) {
        speakText(response.response);
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
      console.error("Error sending message:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = useCallback(async () => {
    try {
      await clearChatHistory(sessionId);
      setMessages({});
      toast.success("Chat history cleared");
    } catch (error) {
      toast.error("Failed to clear chat history");
    }
  }, [sessionId]);

  const handleStartNewSession = useCallback(() => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("hospice_session_id", newSessionId);
    setSessionId(newSessionId);
    setMessages({});
    toast.success("Started new conversation");
  }, []);

  // Voice Recording Functions - SEAMLESS, NEVER REPEATS PROMPTS
  const hasInitialPromptPlayed = useRef(false);
  const lastRecordingResultRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        clearInterval(recordingTimerRef.current);
        
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start(100);
      setIsRecording(true);
      setRecordingDuration(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      // Subtle feedback only - no intrusive prompts
      toast.success(t('chat.recordingStarted', 'Listening...'), { duration: 1500 });
    } catch (err) {
      console.error("Failed to start recording:", err);
      toast.error(t('chat.microphoneDenied', 'Microphone access required'));
    }
  }, [t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  }, [isRecording]);

  // Voice command patterns for different languages
  const getVoiceCommands = useCallback(() => {
    const lang = i18n.language || 'en';
    const commands = {
      en: {
        clearChat: ['clear chat', 'clear history', 'delete chat', 'erase chat'],
        newConversation: ['new conversation', 'new chat', 'start over', 'start new'],
        stopSpeaking: ['stop speaking', 'stop talking', 'be quiet', 'silence'],
        toggleVoice: ['voice on', 'voice off', 'toggle voice', 'mute', 'unmute']
      },
      es: {
        clearChat: ['borrar chat', 'limpiar chat', 'eliminar chat', 'borrar historial'],
        newConversation: ['nueva conversación', 'nuevo chat', 'empezar de nuevo'],
        stopSpeaking: ['deja de hablar', 'silencio', 'para'],
        toggleVoice: ['voz activada', 'voz desactivada', 'silenciar']
      },
      fr: {
        clearChat: ['effacer le chat', 'supprimer le chat', 'effacer l\'historique'],
        newConversation: ['nouvelle conversation', 'nouveau chat', 'recommencer'],
        stopSpeaking: ['arrête de parler', 'silence', 'stop'],
        toggleVoice: ['activer la voix', 'désactiver la voix', 'couper le son']
      },
      zh: {
        clearChat: ['清除聊天', '删除聊天', '清空历史'],
        newConversation: ['新对话', '新聊天', '重新开始'],
        stopSpeaking: ['停止说话', '安静', '停'],
        toggleVoice: ['开启语音', '关闭语音', '静音']
      },
      vi: {
        clearChat: ['xóa chat', 'xóa lịch sử', 'xóa cuộc trò chuyện'],
        newConversation: ['cuộc trò chuyện mới', 'chat mới', 'bắt đầu lại'],
        stopSpeaking: ['ngừng nói', 'im lặng', 'dừng'],
        toggleVoice: ['bật giọng nói', 'tắt giọng nói', 'tắt tiếng']
      },
      ko: {
        clearChat: ['채팅 지우기', '기록 삭제', '채팅 삭제'],
        newConversation: ['새 대화', '새 채팅', '다시 시작'],
        stopSpeaking: ['말 그만', '조용히', '멈춰'],
        toggleVoice: ['음성 켜기', '음성 끄기', '음소거']
      },
      tl: {
        clearChat: ['i-clear ang chat', 'burahin ang chat', 'tanggalin ang chat'],
        newConversation: ['bagong usapan', 'bagong chat', 'magsimula muli'],
        stopSpeaking: ['tumigil ka na', 'tahimik', 'hinto'],
        toggleVoice: ['buksan ang boses', 'patayin ang boses', 'i-mute']
      },
      ar: {
        clearChat: ['مسح الدردشة', 'حذف الدردشة', 'مسح السجل'],
        newConversation: ['محادثة جديدة', 'دردشة جديدة', 'ابدأ من جديد'],
        stopSpeaking: ['توقف عن الكلام', 'صمت', 'قف'],
        toggleVoice: ['تشغيل الصوت', 'إيقاف الصوت', 'كتم']
      }
    };
    return commands[lang] || commands.en;
  }, [i18n.language]);

  // Check if transcribed text matches a voice command
  const processVoiceCommand = useCallback((text) => {
    const normalizedText = text.toLowerCase().trim();
    const commands = getVoiceCommands();
    
    // Check for clear chat command
    if (commands.clearChat.some(cmd => normalizedText.includes(cmd))) {
      handleClearChat();
      toast.success(t('chat.voiceCommand.cleared', 'Chat cleared by voice command'));
      return true;
    }
    
    // Check for new conversation command
    if (commands.newConversation.some(cmd => normalizedText.includes(cmd))) {
      handleStartNewSession();
      toast.success(t('chat.voiceCommand.newStarted', 'New conversation started by voice command'));
      return true;
    }
    
    // Check for stop speaking command (inline logic to avoid circular dependency)
    if (commands.stopSpeaking.some(cmd => normalizedText.includes(cmd))) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
      toast.success(t('chat.voiceCommand.stopped', 'Voice stopped by command'));
      return true;
    }
    
    // Check for toggle voice command (inline logic)
    if (commands.toggleVoice.some(cmd => normalizedText.includes(cmd))) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
      setTtsEnabled(prev => {
        const newValue = !prev;
        toast.success(prev ? t('chat.ttsDisabled', 'Voice responses disabled') : t('chat.ttsEnabled', 'Voice responses enabled'));
        return newValue;
      });
      return true;
    }
    
    return false; // Not a command, treat as regular message
  }, [getVoiceCommands, handleClearChat, handleStartNewSession, t]);

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);

    try {
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = reader.result.split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      const audioBase64 = await base64Promise;

      // Try primary endpoint with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      let response;
      let data;
      
      try {
        response = await fetch(`${API_URL}/api/voice-chat/transcribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            audio_base64: audioBase64,
            context: "chat",
            language: i18n.language || "en"
          }),
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          data = await response.json();
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        console.warn("Primary transcription failed:", fetchError);
        
        // Try fallback endpoint
        try {
          const fallbackResponse = await fetch(`${API_URL}/api/voice/transcribe-base64`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              audio_base64: audioBase64,
              format: "webm",
              language: i18n.language?.split('-')[0] || "en",
              context: "general"
            })
          });
          
          if (fallbackResponse.ok) {
            data = await fallbackResponse.json();
          }
        } catch (fallbackError) {
          console.warn("Fallback transcription failed:", fallbackError);
        }
      }
      
      // Process the result - SEAMLESS, NO REPEATED PROMPTS
      if (data && data.text && data.text.trim() && !data.transcription_failed) {
        const transcribedText = data.text.trim();
        
        // Skip if same as last result (avoid repeats)
        if (transcribedText === lastRecordingResultRef.current) {
          return;
        }
        lastRecordingResultRef.current = transcribedText;
        
        // Check if it's a voice command first
        const isCommand = processVoiceCommand(transcribedText);
        
        if (!isCommand) {
          // Not a command, set as input message silently
          setInputMessage(transcribedText);
          // Subtle confirmation only
          toast.success(t('chat.transcriptionComplete', 'Got it'), { duration: 1000 });
        }
      } else if (data && data.transcription_failed) {
        // SILENT - no repeated prompt, just let user try again
        // Subtle visual indicator only, no error toast
        console.log('Transcription service unavailable');
      } else {
        // No speech detected - SILENT, no repeated prompt
        // User can simply try again if they want
        console.log('No speech detected');
      }
    } catch (err) {
      console.error("Transcription error:", err);
      // SILENT - never show error, user can simply try again
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleVoiceClick = () => {
    if (isRecording) {
      stopRecording();
    } else if (!isTranscribing && !isLoading) {
      startRecording();
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Text-to-Speech Functions
  const speakText = useCallback((text) => {
    if (!ttsEnabled || !('speechSynthesis' in window)) return;
    
    window.speechSynthesis.cancel();
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1;
    utterance.volume = 1;
    
    // Try to match language
    const voices = window.speechSynthesis.getVoices();
    const langCode = i18n.language || 'en';
    const matchingVoice = voices.find(v => v.lang.startsWith(langCode)) || 
                          voices.find(v => v.lang.startsWith('en'));
    if (matchingVoice) {
      utterance.voice = matchingVoice;
    }
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    speechSynthRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, [ttsEnabled, i18n.language]);

  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
    }
  }, []);

  const toggleTts = useCallback(() => {
    if (isSpeaking) {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
      }
    }
    setTtsEnabled(prev => {
      const newValue = !prev;
      toast.success(prev ? t('chat.ttsDisabled', 'Voice responses disabled') : t('chat.ttsEnabled', 'Voice responses enabled'));
      return newValue;
    });
  }, [isSpeaking, t]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const currentContext = chatContexts.find(c => c.id === activeContext);
  const currentMessages = messages[activeContext] || [];

  return (
    <div className="min-h-screen bg-muted/30" data-testid="chat-page">
      <div className="container-max px-4 md:px-12 py-8">
        <div className="grid lg:grid-cols-4 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card className="shadow-soft" data-testid="chat-context-selector">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-serif">{t('chat.topics', 'Chat Topics')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {chatContexts.map((context) => {
                  const Icon = context.icon;
                  return (
                    <button
                      key={context.id}
                      onClick={() => setActiveContext(context.id)}
                      data-testid={`chat-context-${context.id}`}
                      className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${
                        activeContext === context.id
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-muted text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm">{context.label}</p>
                        <p className="text-xs opacity-70">{context.description}</p>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {/* Auth prompt for non-logged in users */}
            {!user && (
              <Card className="shadow-soft bg-primary/5 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <LogIn className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground mb-1">{t('chat.signInSave', 'Sign in to save conversations')}</p>
                      <p className="text-xs text-muted-foreground mb-2">{t('chat.historyPersist', 'Your chat history will persist across sessions')}</p>
                      <Button size="sm" variant="outline" onClick={() => setShowAuthModal(true)} data-testid="chat-signin-btn">
                        {t('nav.signIn')}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Previous conversations (for logged in users) */}
            {user && conversations.length > 0 && (
              <Card className="shadow-soft">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <History className="w-4 h-4" />
                    {t('chat.previousConversations', 'Previous Conversations')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {conversations.slice(0, 5).map((conv, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSessionId(conv.session_id);
                        setActiveContext(conv.context);
                        loadChatHistory();
                      }}
                      className="w-full text-left p-2 rounded-lg hover:bg-muted text-xs"
                    >
                      <p className="font-medium truncate">{conv.last_message}</p>
                      <p className="text-muted-foreground">{chatContexts.find(c => c.id === conv.context)?.label}</p>
                    </button>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleClearChat}
                data-testid="clear-chat-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t('chat.clear', 'Clear')}
              </Button>
              <Button 
                variant="outline" 
                className="flex-1" 
                onClick={handleStartNewSession}
                data-testid="new-chat-btn"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {t('chat.new', 'New')}
              </Button>
            </div>
          </div>

          {/* Chat Area */}
          <div className="lg:col-span-3">
            <Card className="shadow-soft h-[calc(100vh-12rem)]" data-testid="chat-container">
              <CardHeader className="border-b border-border/50 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {currentContext && <currentContext.icon className="w-5 h-5 text-primary" />}
                  </div>
                  <div>
                    <CardTitle className="text-lg font-serif">{currentContext?.label}</CardTitle>
                    <p className="text-sm text-muted-foreground">{currentContext?.description}</p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col h-[calc(100%-8rem)] p-0">
                {/* Messages */}
                <ScrollArea className="flex-1 p-6">
                  <div className="space-y-6">
                    {/* Welcome message */}
                    {currentMessages.length === 0 && (
                      <div className="flex gap-3 animate-fade-in" data-testid="welcome-message">
                        <Avatar className="w-10 h-10 bg-primary/10">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            <Heart className="w-5 h-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="chat-bubble-assistant p-4 max-w-[80%]">
                          <p className="text-foreground leading-relaxed">
                            {currentContext?.welcomeMessage}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Chat messages */}
                    {currentMessages.map((message, index) => (
                      <div 
                        key={message.id || index}
                        className={`flex gap-3 animate-fade-in ${
                          message.role === "user" ? "flex-row-reverse" : ""
                        }`}
                        data-testid={`chat-message-${index}`}
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className={
                            message.role === "user" 
                              ? "bg-secondary/20 text-secondary-foreground" 
                              : "bg-primary/10 text-primary"
                          }>
                            {message.role === "user" 
                              ? <User className="w-5 h-5" /> 
                              : <Heart className="w-5 h-5" />
                            }
                          </AvatarFallback>
                        </Avatar>
                        <div className={`max-w-[80%] ${
                          message.role === "user" ? "chat-bubble-user text-white" : "chat-bubble-assistant"
                        } p-4`}>
                          <p className="leading-relaxed whitespace-pre-wrap">
                            {message.content}
                          </p>
                          <p className={`text-xs mt-2 ${
                            message.role === "user" ? "text-white/70" : "text-muted-foreground"
                          }`}>
                            {formatTime(message.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="flex gap-3 animate-fade-in" data-testid="chat-loading">
                        <Avatar className="w-10 h-10">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            <Heart className="w-5 h-5" />
                          </AvatarFallback>
                        </Avatar>
                        <div className="chat-bubble-assistant p-4">
                          <div className="flex items-center gap-2">
                            <Loader2 className="w-4 h-4 animate-spin text-primary" />
                            <span className="text-muted-foreground">{t('chat.thinking', 'Thinking...')}</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-4 border-t border-border/50">
                  {/* Voice Controls Bar */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {isRecording && (
                        <Badge variant="destructive" className="animate-pulse">
                          <Mic className="w-3 h-3 mr-1" />
                          {t('chat.recording', 'Recording')} {formatDuration(recordingDuration)}
                        </Badge>
                      )}
                      {isTranscribing && (
                        <Badge variant="secondary">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          {t('chat.transcribing', 'Transcribing...')}
                        </Badge>
                      )}
                      {isSpeaking && (
                        <Badge variant="outline" className="text-primary">
                          <Volume2 className="w-3 h-3 mr-1" />
                          {t('chat.speaking', 'Speaking...')}
                        </Badge>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={toggleTts}
                      className={ttsEnabled ? "text-primary" : "text-muted-foreground"}
                      data-testid="tts-toggle-btn"
                    >
                      {ttsEnabled ? (
                        <Volume2 className="w-4 h-4" />
                      ) : (
                        <VolumeX className="w-4 h-4" />
                      )}
                      <span className="ml-1 text-xs">{ttsEnabled ? t('chat.ttsOn', 'Voice On') : t('chat.ttsOff', 'Voice Off')}</span>
                    </Button>
                  </div>
                  
                  <form onSubmit={handleSendMessage} className="flex gap-3" data-testid="chat-input-form">
                    {/* Voice Input Button with Tooltip */}
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant={isRecording ? "destructive" : "outline"}
                            onClick={handleVoiceClick}
                            disabled={isTranscribing || isLoading}
                            className={`h-12 w-12 rounded-xl ${isRecording ? 'animate-pulse' : ''}`}
                            data-testid="voice-input-btn"
                          >
                            {isTranscribing ? (
                              <Loader2 className="w-5 h-5 animate-spin" />
                            ) : isRecording ? (
                              <Square className="w-5 h-5" />
                            ) : (
                              <Mic className="w-5 h-5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p className="font-medium mb-1">{t('chat.voiceCommand.hint', 'Try saying: "clear chat", "new conversation", or "stop speaking"')}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    <Input
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      placeholder={isRecording ? t('chat.speakNow', 'Speak now...') : t('chat.placeholder', 'Type your message or use voice...')}
                      className="flex-1 h-12 rounded-xl"
                      disabled={isLoading || isRecording}
                      data-testid="chat-input"
                    />
                    
                    {/* Stop Speaking Button (when AI is speaking) */}
                    {isSpeaking && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={stopSpeaking}
                        className="h-12 w-12 rounded-xl"
                        data-testid="stop-speaking-btn"
                      >
                        <VolumeX className="w-5 h-5" />
                      </Button>
                    )}
                    
                    <Button 
                      type="submit" 
                      disabled={isLoading || !inputMessage.trim() || isRecording}
                      className="h-12 px-6 rounded-xl bg-primary hover:bg-primary/90"
                      data-testid="chat-send-btn"
                    >
                      {isLoading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Send className="w-5 h-5" />
                      )}
                    </Button>
                  </form>
                  <p className="text-xs text-muted-foreground text-center mt-3">
                    {t('chat.disclaimer', 'This is for informational purposes only. Always consult your hospice team for medical advice.')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
    </div>
  );
}
